import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { parseCustomerNameFromAddress, splitCustomerName } from './dispatch-planner-stop-format';

export interface DeliverySuggestionRow {
  kaufvertragsnummer: string;
  auftragsart: string;
  zahlungsart: string;
  termin: string;
  filiale: string;
  filialbezeichnung: string;
  kundennummer: string;
  vorname: string;
  nachname: string;
  customerName: string;
  telefon: string;
  mobil: string;
  lieferadresse: string;
  rechnungsadresse: string;
  montage: string;
  montagemenge: number;
  lieferfaehigSeit: string;
  zusatztext: string;
  ausgeblendet: string;
}

export function resolveDeliverySuggestionCustomerFields(row: DeliverySuggestionRow): {
  customerName: string;
  nachname: string;
} {
  const explicitNachname = (row.nachname || '').trim();
  const explicitVorname = (row.vorname || '').trim();
  if (explicitNachname || explicitVorname) {
    return {
      customerName: `${explicitVorname} ${explicitNachname}`.trim(),
      nachname: explicitNachname || splitCustomerName(`${explicitVorname} ${explicitNachname}`.trim()).nachname
    };
  }

  const namedCustomer = (row.customerName || '').trim();
  if (namedCustomer) {
    const parsed = splitCustomerName(namedCustomer);
    return {
      customerName: parsed.fullName,
      nachname: parsed.nachname
    };
  }

  const fromRechnung = parseCustomerNameFromAddress(row.rechnungsadresse);
  if (fromRechnung.nachname) {
    return {
      customerName: fromRechnung.fullName,
      nachname: fromRechnung.nachname
    };
  }

  const fromLieferung = parseCustomerNameFromAddress(row.lieferadresse);
  if (fromLieferung.nachname) {
    return {
      customerName: fromLieferung.fullName,
      nachname: fromLieferung.nachname
    };
  }

  return { customerName: '', nachname: '' };
}

@Injectable({
  providedIn: 'root'
})
export class DispatchPlannerDeliverySuggestionService {
  parseWorkbook(buffer: ArrayBuffer): DeliverySuggestionRow[] {
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return [];
    }

    const sheet = workbook.Sheets[sheetName];
    const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    return rawRows
      .map(row => this.mapRow(row))
      .filter(row => !!row.kaufvertragsnummer && !this.isHidden(row));
  }

  collectBranchCodes(rows: DeliverySuggestionRow[]): string[] {
    return Array.from(
      new Set(rows.map(row => (row.filiale || '').trim()).filter(Boolean))
    );
  }

  private mapRow(row: Record<string, unknown>): DeliverySuggestionRow {
    const get = (...keys: string[]) => this.getCell(row, keys);
    const vorname = get('Vorname', 'Kundenvorname');
    const nachname = get('Nachname', 'Kundennachname', 'Familienname');
    const customerName = get('Kundenname', 'Name', 'Kunde', 'Kundenbezeichnung');
    return {
      kaufvertragsnummer: get('Auftragsnummer', 'KV-Nummer', 'Kaufvertragsnummer'),
      auftragsart: get('Auftragsart'),
      zahlungsart: get('Zahlungsart'),
      termin: this.formatDate(get('Kundenwunschtemin', 'Kundenwunschtermin', 'Termin')),
      filiale: get('Filialnummer', 'Filiale'),
      filialbezeichnung: get('Filialbezeichnung'),
      kundennummer: get('Kundennummer'),
      vorname,
      nachname,
      customerName,
      telefon: get('Telefonnummer', 'Telefon'),
      mobil: get('Mobilnummer', 'Mobil', 'Handy'),
      lieferadresse: get('Lieferadresse'),
      rechnungsadresse: get('Rechnungsadresse'),
      montage: get('Montage'),
      montagemenge: this.parseNumber(get('Montageeinheiten')),
      lieferfaehigSeit: this.formatDate(get('Lieferfähig seit', 'Lieferfaehig seit', 'LieferfaehigSeit')),
      zusatztext: get('Zusatztext'),
      ausgeblendet: get('ausgeblendet', 'Ausgeblendet')
    };
  }

  private getCell(row: Record<string, unknown>, keys: string[]): string {
    const normalizedKeys = new Set(keys.map(key => this.normalizeHeader(key)));
    for (const [header, value] of Object.entries(row)) {
      if (normalizedKeys.has(this.normalizeHeader(header))) {
        return this.cellToString(value);
      }
    }
    return '';
  }

  private normalizeHeader(header: string): string {
    return header
      .replace(/^\uFEFF/, '')
      .trim()
      .toLowerCase()
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss');
  }

  private cellToString(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (value instanceof Date) {
      return value.toLocaleDateString('de-DE');
    }
    return String(value).trim();
  }

  private formatDate(value: string): string {
    return (value || '').trim();
  }

  private parseNumber(value: string): number {
    const normalized = (value || '').trim().replace(',', '.');
    if (!normalized) {
      return 0;
    }
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private isHidden(row: DeliverySuggestionRow): boolean {
    return /^ja$/i.test((row.ausgeblendet || '').trim());
  }
}
